import React, { useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { Box } from '@mui/material'

import { Button, Card, Skeleton, Tabs, TABS_CONTENT_GAP } from '@repo/ui'
import { Header } from 'components/navigation'
import { useCanAccess } from 'hooks'
import { useSeoStatus } from 'hooks/api'
import { COMPETITOR_RESEARCH_TITLE, ROUTES } from 'routes'

import { SectionNote } from '../InternalAnalyticsPage/components/shared'
import { SeoCompetitorsCard } from '../InternalAnalyticsPage/components/seo/SeoCompetitorsCard'
import { CompetitorContentTab } from './CompetitorContentTab'
import { CompetitorFeaturesTab } from './CompetitorFeaturesTab'
import { CompetitorPricingTab } from './CompetitorPricingTab'
import { CompetitorSeoTab } from './CompetitorSeoTab'

/**
 * `/internal/competitors` — VRG-744, Competitor research (Spencer, 2026-10-23).
 *
 * One page for what Vera knows about each competitor, grouped with Market research under the
 * sidebar's "Research" section:
 *
 *   Directory  the competitor list and profile — MOVED here from GTM → SEO's Competitors tab, and
 *              the same component (SeoCompetitorsCard), not a copy: `seo_competitors` is the
 *              canonical record and strategy/competition.md is still generated from it.
 *   Pricing    price points per competitor, each with its source page and capture date. A price
 *              change is a new row; the old one is kept as history.
 *   Features   the comparison matrix: a row per capability, Vera's cell (shipped / planned) and a
 *              sourced, dated cell per competitor. Seeded from vera-website content/compare.ts.
 *   Content    competitors' posts in the market-data corpus, newest first.
 *   SEO        each competitor's ranking overlap with Vera and its gap keywords — read from the SEO
 *              tables, never copied. GTM → SEO keeps the Rankings and Gap finder views.
 *
 * `?tab=` is the tab and `?competitor=<domain>` an open profile, so both are linkable
 * (`competitorProfilePath`). Gate: `internal_gtm_view` (routes/internalAccess.ts), the audience the
 * competitor record has always had.
 */

type ResearchTab = 'directory' | 'pricing' | 'features' | 'content' | 'seo'

const TABS: { label: string; value: ResearchTab }[] = [
  { label: 'Directory', value: 'directory' },
  { label: 'Pricing', value: 'pricing' },
  { label: 'Features', value: 'features' },
  { label: 'Content', value: 'content' },
  { label: 'SEO', value: 'seo' },
]

const isTab = (v: string | null): v is ResearchTab => TABS.some((t) => t.value === v)

const DirectoryTab = () => {
  const [params, setParams] = useSearchParams()
  const { data: status, isLoading, error } = useSeoStatus()
  const competitor = params.get('competitor')

  const setProfile = (domain: string | null) => {
    const next = new URLSearchParams(params)
    if (domain) next.set('competitor', domain)
    else next.delete('competitor')
    setParams(next, { replace: true })
  }

  if (isLoading)
    return (
      <Card>
        <Skeleton height={14} lines={8} />
      </Card>
    )
  if (error || !status)
    return (
      <Card>
        <SectionNote>
          {(error as Error | null)?.message ?? 'Could not load competitors.'} Reload, or check the
          API is running.
        </SectionNote>
      </Card>
    )
  return <SeoCompetitorsCard status={status} profileDomain={competitor} onProfileChange={setProfile} />
}

export const CompetitorResearchPage = () => {
  const navigate = useNavigate()
  const { search } = useLocation()
  const params = new URLSearchParams(search)
  const requested = params.get('tab')
  const tab: ResearchTab = isTab(requested) ? requested : 'directory'

  const { isHasAccess: isAdmin } = useCanAccess({ role: 'platform_admin' })
  const [addPriceOpen, setAddPriceOpen] = useState(false)
  const [addFeatureOpen, setAddFeatureOpen] = useState(false)

  const setTab = (next: string) =>
    navigate(next === 'directory' ? ROUTES.INTERNAL_COMPETITORS : `${ROUTES.INTERNAL_COMPETITORS}?tab=${next}`, {
      replace: true,
    })

  const ADMIN_ONLY = 'Only platform admins can change competitor research.'
  /* One primary, rightmost (§26/§33). The Directory keeps its own "Add competitor" in its card. */
  const tabAction =
    tab === 'pricing' ? (
      <Button
        variant="primary"
        disabled={!isAdmin}
        disabledReason={!isAdmin ? ADMIN_ONLY : undefined}
        onClick={() => setAddPriceOpen(true)}
      >
        Add price point
      </Button>
    ) : tab === 'features' ? (
      <Button
        variant="primary"
        disabled={!isAdmin}
        disabledReason={!isAdmin ? ADMIN_ONLY : undefined}
        onClick={() => setAddFeatureOpen(true)}
      >
        Add feature
      </Button>
    ) : null

  return (
    <Box>
      <Header title={COMPETITOR_RESEARCH_TITLE} breadcrumbs={false} />
      <Tabs
        redirect={false}
        tabs={TABS}
        value={tab}
        onChange={setTab}
        rightComponents={tabAction}
        sx={{ mb: `${TABS_CONTENT_GAP}px` }}
      />
      {tab === 'directory' && <DirectoryTab />}
      {tab === 'pricing' && (
        <CompetitorPricingTab addOpen={addPriceOpen} onCloseAdd={() => setAddPriceOpen(false)} />
      )}
      {tab === 'features' && (
        <CompetitorFeaturesTab addOpen={addFeatureOpen} onCloseAdd={() => setAddFeatureOpen(false)} />
      )}
      {tab === 'content' && <CompetitorContentTab />}
      {tab === 'seo' && <CompetitorSeoTab />}
    </Box>
  )
}
